import {BaseBadge, BaseButton, BaseCard} from "@/components";
import {useLevelConfigs, useUpdateLevelConfig} from "@/hooks/useLevels";
import type {LevelConfig, UpdateLevelConfigBody} from "@/types/levels.types";
import {Pencil} from "lucide-react";
import {useState} from "react";
import {LEVEL_BADGE_VARIANT, LEVEL_NAMES, formatCents} from "../levels.utils";

export function LevelConfigsTab() {
  const {data: configs, isLoading} = useLevelConfigs();
  const [editing, setEditing] = useState<LevelConfig | null>(null);

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-3">
        {["sk-cfg-0", "sk-cfg-1", "sk-cfg-2"].map(id => (
          <BaseCard
            key={id}
            padding="md"
          >
            <div className="space-y-3">
              {["a", "b", "c", "d", "e", "f"].map(k => (
                <div
                  key={`${id}-${k}`}
                  className="h-4 bg-(--color-surface-2) rounded animate-pulse"
                />
              ))}
            </div>
          </BaseCard>
        ))}
      </div>
    );
  }

  const items = configs ?? [];

  return (
    <>
      <div className="grid gap-4 md:grid-cols-3">
        {items.map(config => (
          <LevelConfigCard
            key={config.id}
            config={config}
            onEdit={() => setEditing(config)}
          />
        ))}
        {items.length === 0 && (
          <p className="col-span-3 text-center text-(--color-text-tertiary) py-12">No hay configuraciones de nivel.</p>
        )}
      </div>
      {editing && (
        <EditLevelConfigModal
          config={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

/* ── Config Card ───────────────────────────────────────────── */

function LevelConfigCard({config, onEdit}: {config: LevelConfig; onEdit: () => void}) {
  return (
    <BaseCard padding="md">
      <div className="flex items-center justify-between mb-4">
        <BaseBadge
          variant={LEVEL_BADGE_VARIANT[config.level] ?? "neutral"}
          label={LEVEL_NAMES[config.level] ?? config.levelName}
        />
        <BaseButton
          variant="ghost"
          size="sm"
          onClick={onEdit}
          title="Editar"
        >
          <Pencil size={14} />
        </BaseButton>
      </div>

      <div className="space-y-2 text-sm">
        <Row
          label="Mín. envío"
          value={formatCents(config.minSendCents)}
        />
        <Row
          label="Máx. envío"
          value={formatCents(config.maxSendCents)}
        />
        <Row
          label="Máx. recarga"
          value={formatCents(config.maxRechargeCents)}
        />
        <Divider />
        <Row
          label="Límite diario"
          value={formatCents(config.dailyLimitCents)}
        />
        <Row
          label="Límite semanal"
          value={formatCents(config.weeklyLimitCents)}
        />
        <Row
          label="Límite mensual"
          value={formatCents(config.monthlyLimitCents)}
        />
        <Divider />
        <Row
          label="Recargas/día"
          value={String(config.maxRechargesPerDay)}
        />
        <Row
          label="Envíos/día"
          value={String(config.maxSendsPerDay)}
        />
        <Row
          label="Maduración (hrs)"
          value={String(config.maturationHours)}
        />
        <Row
          label="Benef. nuevos/semana"
          value={String(config.maxNewBeneficiariesPerWeek)}
        />
        <Divider />
        <FeatureRow
          label="Tarjeta"
          enabled={config.cardEnabled}
        />
        <FeatureRow
          label="Express"
          enabled={config.expressEnabled}
        />
        <FeatureRow
          label="Inmediato"
          enabled={config.immediateEnabled}
        />
      </div>
    </BaseCard>
  );
}

function Row({label, value}: {label: string; value: string}) {
  return (
    <div className="flex justify-between">
      <span className="text-(--color-text-secondary)">{label}</span>
      <span className="font-medium text-(--color-text-primary)">{value}</span>
    </div>
  );
}

function FeatureRow({label, enabled}: {label: string; enabled: boolean}) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-(--color-text-secondary)">{label}</span>
      <BaseBadge
        variant={enabled ? "success" : "neutral"}
        label={enabled ? "Sí" : "No"}
      />
    </div>
  );
}

function Divider() {
  return <div className="border-t border-(--color-border) my-1" />;
}

/* ── Edit Level Config Modal ───────────────────────────────── */

function EditLevelConfigModal({config, onClose}: {config: LevelConfig; onClose: () => void}) {
  const updateMutation = useUpdateLevelConfig();
  const [form, setForm] = useState({
    minSendCents: String(Number(config.minSendCents) / 100),
    maxSendCents: String(Number(config.maxSendCents) / 100),
    maxRechargeCents: String(Number(config.maxRechargeCents) / 100),
    dailyLimitCents: String(Number(config.dailyLimitCents) / 100),
    weeklyLimitCents: String(Number(config.weeklyLimitCents) / 100),
    monthlyLimitCents: String(Number(config.monthlyLimitCents) / 100),
    maxRechargesPerDay: String(config.maxRechargesPerDay),
    maxSendsPerDay: String(config.maxSendsPerDay),
    maturationHours: String(config.maturationHours),
    maxNewBeneficiariesPerWeek: String(config.maxNewBeneficiariesPerWeek),
    cardEnabled: config.cardEnabled,
    expressEnabled: config.expressEnabled,
    immediateEnabled: config.immediateEnabled,
  });
  const [error, setError] = useState("");

  const set = (key: string, value: string | boolean) => setForm(prev => ({...prev, [key]: value}));

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError("");

    const body: UpdateLevelConfigBody = {
      minSendCents: String(Math.round(Number(form.minSendCents) * 100)),
      maxSendCents: String(Math.round(Number(form.maxSendCents) * 100)),
      maxRechargeCents: String(Math.round(Number(form.maxRechargeCents) * 100)),
      dailyLimitCents: String(Math.round(Number(form.dailyLimitCents) * 100)),
      weeklyLimitCents: String(Math.round(Number(form.weeklyLimitCents) * 100)),
      monthlyLimitCents: String(Math.round(Number(form.monthlyLimitCents) * 100)),
      maxRechargesPerDay: Number(form.maxRechargesPerDay),
      maxSendsPerDay: Number(form.maxSendsPerDay),
      maturationHours: Number(form.maturationHours),
      maxNewBeneficiariesPerWeek: Number(form.maxNewBeneficiariesPerWeek),
      cardEnabled: form.cardEnabled,
      expressEnabled: form.expressEnabled,
      immediateEnabled: form.immediateEnabled,
    };

    try {
      await updateMutation.mutateAsync({level: config.level, body});
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al actualizar la configuración");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-(--color-text-primary) mb-4">
          Editar — {LEVEL_NAMES[config.level] ?? config.levelName}
        </h2>
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-4"
        >
          <p className="text-xs text-(--color-text-tertiary) -mt-2">
            Los montos se ingresan en dólares (se convierten a centavos automáticamente).
          </p>

          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Mín. envío ($)"
              value={form.minSendCents}
              onChange={v => set("minSendCents", v)}
              type="number"
              step="0.01"
            />
            <Field
              label="Máx. envío ($)"
              value={form.maxSendCents}
              onChange={v => set("maxSendCents", v)}
              type="number"
              step="0.01"
            />
          </div>
          <Field
            label="Máx. recarga ($)"
            value={form.maxRechargeCents}
            onChange={v => set("maxRechargeCents", v)}
            type="number"
            step="0.01"
          />

          <div className="grid grid-cols-3 gap-3">
            <Field
              label="Límite diario ($)"
              value={form.dailyLimitCents}
              onChange={v => set("dailyLimitCents", v)}
              type="number"
              step="0.01"
            />
            <Field
              label="Límite semanal ($)"
              value={form.weeklyLimitCents}
              onChange={v => set("weeklyLimitCents", v)}
              type="number"
              step="0.01"
            />
            <Field
              label="Límite mensual ($)"
              value={form.monthlyLimitCents}
              onChange={v => set("monthlyLimitCents", v)}
              type="number"
              step="0.01"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Recargas/día"
              value={form.maxRechargesPerDay}
              onChange={v => set("maxRechargesPerDay", v)}
              type="number"
            />
            <Field
              label="Envíos/día"
              value={form.maxSendsPerDay}
              onChange={v => set("maxSendsPerDay", v)}
              type="number"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Maduración (hrs)"
              value={form.maturationHours}
              onChange={v => set("maturationHours", v)}
              type="number"
            />
            <Field
              label="Benef./semana"
              value={form.maxNewBeneficiariesPerWeek}
              onChange={v => set("maxNewBeneficiariesPerWeek", v)}
              type="number"
            />
          </div>

          <div className="flex flex-wrap gap-4 py-2">
            <Toggle
              label="Tarjeta"
              checked={form.cardEnabled}
              onChange={v => set("cardEnabled", v)}
            />
            <Toggle
              label="Express"
              checked={form.expressEnabled}
              onChange={v => set("expressEnabled", v)}
            />
            <Toggle
              label="Inmediato"
              checked={form.immediateEnabled}
              onChange={v => set("immediateEnabled", v)}
            />
          </div>

          {error && <p className="text-sm text-(--color-danger)">{error}</p>}

          <div className="flex justify-end gap-2 mt-2">
            <BaseButton
              variant="ghost"
              size="md"
              onClick={onClose}
            >
              Cancelar
            </BaseButton>
            <BaseButton
              variant="primary"
              size="md"
              loading={updateMutation.isPending}
            >
              Guardar
            </BaseButton>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  step,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  step?: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-(--color-text-secondary)">{label}</span>
      <input
        type={type}
        step={step}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="px-3 py-2 rounded-md border border-(--color-border) text-sm"
      />
    </label>
  );
}

function Toggle({label, checked, onChange}: {label: string; checked: boolean; onChange: (v: boolean) => void}) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className="w-4 h-4 rounded border-(--color-border) accent-(--color-primary)"
      />
      <span className="text-sm text-(--color-text-secondary)">{label}</span>
    </label>
  );
}
